<template>
  <div v-if="data" class="weapon-specifications hm">
    <SpecCategories
        v-if="showCategories"
        :filters="filters"
    />

    <SpecRarities
        v-if="filters.rarities_count?.length > 1"
        :filters="filters"
    />

    <div v-if="data?.characteristics?.body_damage" class="weapon-specifications__item">
      <p class="item-title mb8">
        {{ $t('damage_map') }}
      </p>
      <!--      <client-only>-->
      <DamageMap :item="data" :name="name" />
      <!--      </client-only>-->
    </div>

    <div v-if="data?.have_spray" class="weapon-specifications__item">
      <p class="item-title mb8">
        {{ $t('spray') }}
      </p>
      <!--      <client-only>-->
      <LazyPagesWeaponComponentsSpray :name="name" />
      <!--      </client-only>-->
    </div>
  </div>

  <div class="weapon-specifications mobile ht hd">
    <div v-if="showCategories"
         class="weapon-specifications__item"
    >
      <UiCollapse>
        <template #label>
          <span class="f gap8">
            <svg height="20" width="20">
              <use xlink:href="/icons/sprite.svg#i-category"></use>
            </svg>
            <span class="item-title">
              {{ $t('categories_label') }}
            </span>
          </span>
        </template>
        <template #items>
          <div class="item-labels mt16">
            <button
                v-if="filters.normal_count"
                :class="{active: activeFilters.extra === 'NORMAL'}"
                class="item-labels-item btn btn-primary-stroke"
                @click="setFilter('extra', 'NORMAL')"
            >
              <span class="item-labels-left">{{ $t('normal') }}</span>
              <span class="item-labels-right">
                <template v-if="activeFilters.extra !== 'NORMAL'">
                  {{ filters.normal_count || 0 }}
                </template>
                <i v-else class="i-times"></i>
              </span>
            </button>
            <button
                v-if="filters.stattrak_count"
                :class="{active: activeFilters.extra === 'STATTRAK'}"
                class="item-labels-item btn btn-primary-stroke"
                @click="setFilter('extra', 'STATTRAK')"
            >
              <span class="item-labels-left stattrak">StatTrak™ </span>
              <span class="item-labels-right">
                 <template v-if="activeFilters.extra !== 'STATTRAK'">
                    {{ filters.stattrak_count || 0 }}
                  </template>
                  <i v-else class="i-times"></i>
              </span>
            </button>
            <button
                v-if="filters.souvenir_count"
                :class="{active: activeFilters.extra === 'SOUVENIR'}"
                class="item-labels-item btn btn-primary-stroke"
                @click="setFilter('extra', 'SOUVENIR')"
            >
              <span class="item-labels-left souvenir">Souvenir</span>
              <span class="item-labels-right">
                 <template v-if="activeFilters.extra !== 'SOUVENIR'">
                    {{ filters.souvenir_count || 0 }}
                  </template>
                  <i v-else class="i-times"></i>
                </span>
            </button>
          </div>
        </template>
      </UiCollapse>
    </div>

    <!--    <div class="hr"></div>-->

    <div
        v-if="filters.rarities_count?.length"
        class="weapon-specifications__item"
    >
      <UiCollapse>
        <template #label>
          <span class="f gap8">
            <svg height="20" width="20">
              <use xlink:href="/icons/sprite.svg#i-rarity"></use>
            </svg>
            <span class="item-title">
              {{ $t('Rarity') }}
            </span>
          </span>
        </template>
        <template #items>
          <div class="item-labels mt16">
            <button
                v-for="rarity in filters.rarities_count"
                :key="rarity?.name"
                :class="{active: activeFilters.rarity === rarity?.name}"
                class="item-labels-item btn btn-primary-stroke"
                @click="setFilter('rarity', rarity?.name || '')"
            >
            <span :class="`circle-${getModifiedString(rarity?.name)}`"
                  class="item-labels-left circle">
              {{ $t(`filters.rarity.${rarity?.name}`) }}
            </span>
              <span class="item-labels-right">
                 <template v-if="activeFilters.rarity !== rarity?.name">
                    {{ rarity?.count || 0 }}
                  </template>
                  <i v-else class="i-times"></i>
              </span>
            </button>
          </div>
        </template>
      </UiCollapse>
    </div>

    <!--    <div class="hr"></div>-->

    <div
        v-if="data?.characteristics?.body_damage"
        class="weapon-specifications__item"
    >
      <UiCollapse>
        <template #label>
          <span class="f gap8">
            <svg height="20" width="20">
              <use xlink:href="/icons/sprite.svg#i-lightning"></use>
            </svg>
            <span class="item-title">
              {{ $t('damage_map') }}
            </span>
          </span>
        </template>
        <template #items>
          <!--          <client-only>-->
          <DamageMap :item="data" :name="name" class="mt16" />
          <!--          </client-only>-->
        </template>
      </UiCollapse>
    </div>

    <template v-if="data?.have_spray">
      <!--      <div class="hr"></div>-->

      <div class="weapon-specifications__item">
        <UiCollapse>
          <template #label>
            <span class="f gap8">
              <svg height="20" width="20">
                <use xlink:href="/icons/sprite.svg#i-spray"></use>
              </svg>
              <span class="item-title">
                {{ $t('spray') }}
              </span>
            </span>
          </template>
          <template #items>
            <!--            <client-only>-->
            <LazyPagesWeaponComponentsSpray :name="name" class="mt16" />
            <!--            </client-only>-->
          </template>
        </UiCollapse>
      </div>
    </template>
  </div>
</template>

<script lang="ts" setup>
import type {WeapoData} from "~/types/WeapoData";
import DamageMap from "~/components/Pages/Weapon/Components/DamageMap.vue";
import type {ItemWeaponPageFiltersResult} from "~/graphql/generated/schema-types";
import SpecCategories from "~/components/Pages/Weapon/Components/SpecCategories.vue";
import SpecRarities from "~/components/Pages/Weapon/Components/SpecRarities.vue";

const props = defineProps<{
  data: WeapoData,
  name: string,
  filters: ItemWeaponPageFiltersResult
}>()

const router = useRouter()
const route = useRoute()
const showCategories = computed(() => {
  const filters = props.filters;
  return [filters.normal_count, filters.souvenir_count, filters.stattrak_count].filter(count => !!count)?.length > 1
})

const activeFilters = computed(() => {
  const extra = route.query['extra'];
  const rarity = route.query['rarity'];
  return {
    extra,
    rarity
  }
})


const setFilter = (name: string, value: string | null) => {
  const newQuery = {...route.query}

  if (value === null || value === newQuery[name]) {
    delete newQuery[name]
  } else {
    newQuery[name] = value
  }

  router.push({query: newQuery})
}
</script>

<style lang="scss" scoped>


.weapon-specifications {
  flex-grow: 0;
  display: flex;
  flex-direction: column;
  gap: 3.2rem;
  color: var(--surfaces-on-surface);
  //min-width: 280px;
  .btn.clear-btn {
    min-height: 3rem;
    padding: 0 8px;
    font-size: 12px;
    font-weight: 600;
  }

  &:deep(.weapon-specifications__item),
  &__item {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 0 16px;

    .item {
      &-title {
        font-size: 1.8rem;
        font-weight: 800;
      }

      &-labels {
        display: flex;
        flex-direction: column;
        gap: 8px;

        &-item {
          width: 100%;
          display: flex;
          justify-content: space-between;
          align-items: center;
          font-size: 1.4rem;
          padding: 12px 0;

          &.btn {
            padding: 12px 8px;

            &.active {
              border-color: var(--accent-secondary)
            }
          }
        }

        &-left {
          font-weight: 600;

          &.circle {
            display: flex;
            align-items: center;
            gap: 8px;

            &:before {
              content: '';
              display: inline-block;
              width: 16px;
              height: 16px;
              border-radius: 50%;
              background-color: var(--destructive);
            }

            &.circle-covert:before,
            &.circle-remarkable:before,
            &.circle-distinguished:before {
              background-color: var(--destructive);
            }

            &.circle-classified:before,
            &.circle-exotic:before,
            &.circle-exceptional:before {
              background-color: #D63EEA;
            }

            &.circle-restricted:before,
            &.circle-high-grade:before,
            &.circle-master:before {
              background-color: #9861FF;
            }

            &.circle-mil-spec-grade:before,
            &.circle-mil-spec:before,
            &.circle-superior:before {
              background-color: #617BFF;
            }

            &.circle-industrial:before,
            &.circle-industrial-grade:before,
            &.circle-extraordinary:before {
              background-color: #5E98D9;
            }

            &.circle-consumer:before,
            &.circle-consumer-grade:before,
            &.circle-default:before {
              background-color: #B0C3D9;
            }
          }
        }

        &-right {
          color: var(--surfaces-on-surface-variable);
          font-weight: 500;
        }
      }
    }
  }

  @media screen and (max-width: 1075px) {
    width: 320px;
  }
  @media screen and (max-width: 768px) {
    width: 100%;
    padding: 0 16px;
    gap: 10px;
    margin-top: 12px;

    &__item {
      padding: 0;

      svg {
        color: var(--accent-secondary);
      }

      .item-title {
        font-size: 16px;
        font-weight: 600;
        color: inherit;
      }
    }
  }
}
</style>
